import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import { useIsRestaurant } from '../../../hooks/useAccountType';
import {
  useGetPlaylists,
  useCreatePlaylist,
  useDeletePlaylist,
} from '../../../queries/wallPlaylistLibrary';
import { useGetAssignments } from '../../../queries/wallAssignments';
import AssignDialog from './AssignDialog';

/**
 * Content Library — Phase 1 of the restaurant-chain platform redesign
 * (.claude/PLATFORM_DESIGN.md). Standalone, reusable playlists decoupled from any
 * single wall. This phase covers list + create/edit metadata + delete; the
 * full visual item editor (drag-drop, media drawer, split grid) lands in Phase 2,
 * and assigning a playlist to walls lands in Phase 3.
 */
export default function VideoWallLibrary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRestaurant = useIsRestaurant();

  const { data: playlists = [], isLoading } = useGetPlaylists();
  const { data: assignments = [] } = useGetAssignments();
  const create = useCreatePlaylist();
  const del = useDeletePlaylist();

  const [assignFor, setAssignFor] = useState(null);
  // distinct wall count per playlist (for the "N walls" badge)
  const wallCount = useMemo(() => {
    const sets = {};
    assignments.forEach((a) => { (sets[a.playlist_id] ||= new Set()).add(a.assignable_id); });
    return Object.fromEntries(Object.entries(sets).map(([k, v]) => [k, v.size]));
  }, [assignments]);

  // Quick "create" dialog only (name + description). Full content editing — items,
  // drag-reorder, media — lives on the dedicated editor page (Phase 2).
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const openCreate = () => { setCreating(true); setName(''); setDescription(''); };
  const close = () => setCreating(false);
  const openEditor = (id) => navigate(`/apps/video-wall/library/${id}`);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const res = await create.mutateAsync({ name: trimmed, description: description.trim() || null });
    const body = res?.data?.data ?? res?.data;
    const newId = body?.playlist?.id;
    close();
    if (newId) openEditor(newId); // jump straight into adding content
  };

  const confirmDelete = (p) =>
    confirmDialog({
      message: t('video_wall.library.delete_confirm', { name: p.name }),
      header: t('video_wall.library.delete_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('video_wall.library.delete'),
      rejectLabel: t('video_wall.library.cancel'),
      accept: () => del.mutate(p.id),
    });

  const saving = create.isLoading;

  return (
    <Layout>
      <ConfirmDialog />

      {/* Hero */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="fas fa-photo-video me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.library.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>
            {t('video_wall.library.subtitle')}
          </p>
        </div>
        <div className="d-flex gap-2">
          {!isRestaurant && (
          <Button
            label={t('video_wall.library.back_to_walls')}
            icon="pi pi-th-large"
            outlined
            onClick={() => navigate('/apps/video-wall')}
          />
          )}
          <Button
            label={t('video_wall.library.create')}
            icon="pi pi-plus"
            onClick={openCreate}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="row g-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="col-12 col-md-6 col-lg-4"><Skeleton height="9rem" /></div>
          ))}
        </div>
      ) : playlists.length === 0 ? (
        <div
          className="text-center p-5"
          style={{
            background: 'var(--pp-bg-secondary)',
            border: '1px dashed var(--pp-border)',
            borderRadius: 'var(--pp-radius-lg)',
          }}
        >
          <div style={{ fontSize: '3rem' }}>🎞️</div>
          <h4 className="mt-3" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.library.empty_title')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.library.empty_subtitle')}</p>
          <Button label={t('video_wall.library.create')} icon="pi pi-plus" onClick={openCreate} />
        </div>
      ) : (
        <div className="row g-3">
          {playlists.map((p) => (
            <div key={p.id} className="col-12 col-md-6 col-lg-4">
              <div
                className="pp-card-clickable h-100 p-3 d-flex flex-column"
                style={{
                  background: 'var(--pp-bg-elevated)',
                  border: '1px solid var(--pp-border)',
                  borderRadius: 'var(--pp-radius-lg)',
                  boxShadow: 'var(--pp-shadow-sm)',
                }}
                role="button" tabIndex={0}
                onClick={() => openEditor(p.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditor(p.id); } }}
              >
                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                  <h5 className="mb-0 text-truncate" style={{ color: 'var(--pp-text-primary)' }}>{p.name}</h5>
                  <div className="d-flex gap-1 align-items-center" style={{ flexShrink: 0 }}>
                    <span
                      style={{
                        background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)',
                        fontWeight: 600, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap',
                      }}
                    >
                      {t('video_wall.library.items_count', { n: p.items_count ?? 0 })}
                    </span>
                    <span
                      style={{
                        background: wallCount[p.id] ? 'var(--pp-btn-honey)' : 'var(--pp-bg-tertiary)',
                        color: wallCount[p.id] ? '#fff' : 'var(--pp-text-secondary)',
                        fontWeight: 600, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap',
                      }}
                    >
                      {wallCount[p.id]
                        ? t('video_wall.library.assign.walls_count', { n: wallCount[p.id] })
                        : t('video_wall.library.assign.not_assigned')}
                    </span>
                  </div>
                </div>

                <p
                  className="mb-0 flex-grow-1"
                  style={{
                    color: 'var(--pp-text-secondary)', fontSize: 13, minHeight: '2.4em',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}
                >
                  {p.description || t('video_wall.library.no_description')}
                </p>

                <div className="pp-card-actions mt-3 pt-2">
                  <Button
                    size="small" icon="pi pi-pencil" label={t('video_wall.edit')}
                    onClick={(e) => { e.stopPropagation(); openEditor(p.id); }}
                  />
                  <div className="pp-card-actions-end">
                    <Button
                      icon="pi pi-sitemap" rounded text size="small"
                      aria-label={t('video_wall.library.assign.assign')}
                      onClick={(e) => { e.stopPropagation(); setAssignFor(p); }}
                    />
                    <Button
                      className="pp-card-del" icon="pi pi-trash" rounded text severity="danger" size="small"
                      aria-label={t('video_wall.library.delete')}
                      onClick={(e) => { e.stopPropagation(); confirmDelete(p); }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick create dialog (full content editing happens on the editor page) */}
      <Dialog
        visible={creating}
        onHide={close}
        header={t('video_wall.library.create_title')}
        style={{ width: 'min(94vw, 480px)' }}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button label={t('video_wall.library.cancel')} text onClick={close} disabled={saving} />
            <Button
              label={t('video_wall.library.save')}
              icon="pi pi-check"
              onClick={save}
              loading={saving}
              disabled={!name.trim()}
            />
          </div>
        }
      >
        <div className="d-flex flex-column gap-3 pt-2">
          <div>
            <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>
              {t('video_wall.library.name')}
            </label>
            <InputText
              className="w-100"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('video_wall.library.name_ph')}
              maxLength={150}
              autoFocus
            />
          </div>
          <div>
            <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>
              {t('video_wall.library.description')}
            </label>
            <InputTextarea
              className="w-100"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('video_wall.library.description_ph')}
              rows={3}
              maxLength={500}
              autoResize
            />
          </div>
          <small style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-info-circle me-1" />
            {t('video_wall.library.create_then_edit_hint')}
          </small>
        </div>
      </Dialog>

      <AssignDialog playlist={assignFor} visible={!!assignFor} onHide={() => setAssignFor(null)} />
    </Layout>
  );
}
